import { useCallback } from "react";
import type React from "react";
import { flushSync } from "react-dom";
import { useNavigate } from "react-router-dom";

// Transiciones entre páginas de la vista de cliente (View Transitions del
// navegador). Hacia dentro, lo pulsado (una fila, un enlace del sidebar) se
// abre hasta ser la página y su nombre viaja hasta el título. Hacia fuera, la
// página se cierra en la fila de la que salió. Sin soporte, o con movimiento
// reducido, se navega sin más.
//
// Un nombre de transición no puede estar dos veces en pantalla: las páginas
// llevan nombres fijos (contenido y título) y, al pulsar algo, se apagan los
// de la página que se va y se nombra solo lo pulsado. Al acabar se quitan los
// nombres puestos a mano (el sidebar no se desmonta y los conservaría).

/** El nombre de lo pulsado y el título de la página. */
export const TITLE_TRANSITION = "client-title";
/** Lo pulsado y el contenido de la página. */
export const PAGE_TRANSITION = "client-page";

/** Marca del nombre dentro de lo que se pulsa. */
export const TITLE_SOURCE_ATTR = "data-client-title";
/** Fila de una campaña o subcampaña, con su id: donde se cierra la página. */
export const ROW_ATTR = "data-client-row";
/** Piezas con nombre fijo de la página (contenido y título). */
export const PAGE_TARGET_ATTR = "data-client-page-target";
/** En <html> mientras dura la transición: "back" cuando la página se cierra. */
export const TRANSITION_DIRECTION_ATTR = "data-client-transition";

const NAME = "view-transition-name";

const prefersReducedMotion = () =>
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;

/** Clic normal: no es para abrir en otra pestaña o ventana. */
export const isPlainClick = (event: React.MouseEvent) =>
  event.button === 0 &&
  !event.metaKey &&
  !event.ctrlKey &&
  !event.shiftKey &&
  !event.altKey;

export interface ClientTransitionOptions {
  /** Lo pulsado: se abre hasta ser la página nueva. */
  from?: HTMLElement | null;
  /** Id de la fila (en la página nueva) en la que se cierra la actual. */
  returnTo?: string | null;
}

/** Navega con transición: `go(to, { from })` hacia dentro, `{ returnTo }` hacia fuera. */
export const useClientTransition = () => {
  const navigate = useNavigate();
  return useCallback(
    (to: string, { from, returnTo }: ClientTransitionOptions = {}) => {
      if (!document.startViewTransition || prefersReducedMotion()) {
        navigate(to);
        return;
      }
      const touched: HTMLElement[] = [];
      const name = (el: HTMLElement, value: string) => {
        el.style.setProperty(NAME, value);
        touched.push(el);
      };
      const muteTargets = () =>
        document
          .querySelectorAll<HTMLElement>(`[${PAGE_TARGET_ATTR}]`)
          .forEach((el) => name(el, "none"));
      const nameAsSource = (el: HTMLElement) => {
        name(el, PAGE_TRANSITION);
        const title = el.querySelector<HTMLElement>(`[${TITLE_SOURCE_ATTR}]`);
        if (title) name(title, TITLE_TRANSITION);
      };

      if (from) {
        muteTargets();
        nameAsSource(from);
      }
      const root = document.documentElement;
      root.setAttribute(TRANSITION_DIRECTION_ATTR, returnTo ? "back" : "in");
      const clearNames = () => {
        touched.forEach((el) => el.style.removeProperty(NAME));
        touched.length = 0;
      };
      const transition = document.startViewTransition(() => {
        flushSync(() => navigate(to));
        // La página de antes ya está fotografiada con sus nombres. Lo que sigue
        // en pantalla (el sidebar, o la misma página con otra campaña) no puede
        // conservarlos: chocarían con los de la nueva y el navegador abortaría
        // la transición ("invalid state").
        clearNames();
        if (!returnTo) return;
        // Ya está la página nueva: si se ve la fila de la que se salió, la
        // página se cierra en ella en vez de en el contenido nuevo.
        const row = document.querySelector<HTMLElement>(
          `[${ROW_ATTR}="${returnTo}"]`
        );
        const rect = row?.getBoundingClientRect();
        if (!row || !rect || rect.bottom <= 0 || rect.top >= window.innerHeight)
          return;
        muteTargets();
        nameAsSource(row);
      });
      const cleanUp = () => {
        clearNames();
        root.removeAttribute(TRANSITION_DIRECTION_ATTR);
      };
      if (transition?.finished)
        transition.finished.catch(() => undefined).finally(cleanUp);
      else cleanUp();
    },
    [navigate]
  );
};

/**
 * Props para un enlace (RouterLink) que navega con transición: el clic normal
 * la hace; con Ctrl, Cmd o rueda sigue abriendo otra pestaña.
 */
export const transitionLinkProps = (
  go: ReturnType<typeof useClientTransition>,
  to: string,
  options: Omit<ClientTransitionOptions, "from"> & { expand?: boolean } = {}
) => ({
  onClick: (event: React.MouseEvent<HTMLElement>) => {
    if (!isPlainClick(event)) return;
    event.preventDefault();
    go(to, {
      from: options.expand ? event.currentTarget : null,
      returnTo: options.returnTo,
    });
  },
});
